import React, { useState } from 'react';
import { PriceList, ContractLine } from '../types';
import Modal from './Modal';
import { parseAmount } from '../utils/amountParser';

interface BulkEditField {
  enabled: boolean;
  value: any;
}

interface BulkEditFields {
  pricelist: BulkEditField;
  customAmount: BulkEditField;
  taxRate: BulkEditField;
  discount: {
    enabled: boolean;
    type: 'percentage' | 'fixed';
    value: number;
  };
  startDate: BulkEditField;
  endDate: BulkEditField;
  trialPeriod: BulkEditField;
}

interface BulkEditContractLinesModalProps {
  contractLines: ContractLine[];
  priceLists: PriceList[];
  onSubmit: (updates: Array<{
    contractLineId: string;
    updates: {
      pricelist_id?: number;
      custom_amount?: number | null;
      tax_rate?: number;
      discount_type?: string;
      discount_value?: number;
      start?: string;
      end?: string;
      trial_period?: number;
    };
  }>) => void;
  onCancel: () => void;
}

const BulkEditContractLinesModal: React.FC<BulkEditContractLinesModalProps> = ({
  contractLines,
  priceLists,
  onSubmit,
  onCancel
}) => {
  const [bulkEditFields, setBulkEditFields] = useState<BulkEditFields>({
    pricelist: { enabled: false, value: null },
    customAmount: { enabled: false, value: '' },
    taxRate: { enabled: false, value: 19 }, // Default 19% tax
    discount: { enabled: false, type: 'percentage', value: 0 },
    startDate: { enabled: false, value: '' },
    endDate: { enabled: false, value: '' },
    trialPeriod: { enabled: false, value: 0 }
  });

  // Get compatible price lists for bulk edit (must be compatible with all selected lines)
  const getCompatiblePriceLists = () => {
    // Get all unique billing cycles from selected lines
    const billingCycles = [...new Set(contractLines.map(line => line.billing_cycle))];
    
    // If lines have different billing cycles, no price lists are compatible
    if (billingCycles.length > 1) {
      return [];
    }
    
    const billingCycle = billingCycles[0];
    
    // Strict mapping: monthly contracts can only have monthly pricelists, yearly can only have yearly
    const cycleMapping: { [key: string]: string[] } = {
      '1_month': ['1_month'],
      '3_months': ['3_months'],
      '6_months': ['6_months'],
      '12_months': ['12_months'],
      '24_months': ['24_months'],
    };

    const compatiblePeriods = cycleMapping[billingCycle] || [];
    
    return priceLists.filter(pl => 
      pl.is_active && 
      compatiblePeriods.includes(pl.billing_period)
    );
  };

  const handleFieldToggle = (field: keyof BulkEditFields) => {
    if (field === 'discount') {
      setBulkEditFields(prev => ({
        ...prev,
        discount: {
          ...prev.discount,
          enabled: !prev.discount.enabled
        }
      }));
    } else {
      setBulkEditFields(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          enabled: !prev[field].enabled
        }
      }));
    }
  };

  const handleFieldChange = (field: keyof BulkEditFields, value: any) => {
    if (field === 'discount') {
      // Handle discount separately
      return;
    }
    
    setBulkEditFields(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value: value
      }
    }));
  };

  const handleDiscountChange = (type: 'type' | 'value', value: any) => {
    setBulkEditFields(prev => ({
      ...prev,
      discount: {
        ...prev.discount,
        [type]: value
      }
    }));
  };

  const handleSubmit = () => {
    // Validate date logic if both dates are enabled
    if (bulkEditFields.startDate.enabled && bulkEditFields.endDate.enabled && 
        bulkEditFields.startDate.value && bulkEditFields.endDate.value) {
      const startDate = new Date(bulkEditFields.startDate.value);
      const endDate = new Date(bulkEditFields.endDate.value);
      
      if (endDate <= startDate) {
        alert('End date must be after start date.');
        return;
      }
    }

    const updates = contractLines.map(line => {
      const lineUpdates: any = {};
      
      if (bulkEditFields.pricelist.enabled && bulkEditFields.pricelist.value) {
        lineUpdates.pricelist_id = parseInt(bulkEditFields.pricelist.value);
      }
      
      if (bulkEditFields.customAmount.enabled) {
        const customValue = bulkEditFields.customAmount.value;
        lineUpdates.custom_amount = parseAmount(customValue);
      }
      
      if (bulkEditFields.taxRate.enabled) {
        lineUpdates.tax_rate = parseFloat(bulkEditFields.taxRate.value);
      }
      
      if (bulkEditFields.discount.enabled) {
        lineUpdates.discount_type = bulkEditFields.discount.type;
        lineUpdates.discount_value = bulkEditFields.discount.type === 'percentage' 
          ? Math.round(bulkEditFields.discount.value * 100) // Store percentage as integer (e.g., 10% = 1000)
          : Math.round(bulkEditFields.discount.value * 100); // Store fixed amount in cents
      }
      
      if (bulkEditFields.startDate.enabled && bulkEditFields.startDate.value) {
        lineUpdates.start = bulkEditFields.startDate.value;
      }
      
      if (bulkEditFields.endDate.enabled && bulkEditFields.endDate.value) {
        lineUpdates.end = bulkEditFields.endDate.value;
      }
      
      if (bulkEditFields.trialPeriod.enabled) {
        lineUpdates.trial_period = parseInt(bulkEditFields.trialPeriod.value) || 0;
      }
      
      return {
        contractLineId: line.contractline_id,
        updates: lineUpdates
      };
    });
    
    // Filter out updates with no changes
    const validUpdates = updates.filter(update => Object.keys(update.updates).length > 0);
    
    if (validUpdates.length === 0) {
      alert('No changes were made. Please enable at least one field to edit.');
      return;
    }
    
    onSubmit(validUpdates);
  };

  const compatiblePriceLists = getCompatiblePriceLists();
  const hasMultipleBillingCycles = [...new Set(contractLines.map(line => line.billing_cycle))].length > 1;

  return (
    <Modal
      title={`Bulk Edit ${contractLines.length} Contract Lines`}
      onClose={onCancel}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <div className="alert alert-info" style={{ marginBottom: '20px' }}>
          <strong>Note:</strong> Only the fields you enable will be updated. Disabled fields will remain unchanged.
        </div>

        {/* Selected Contract Lines */}
        <div style={{ marginBottom: '20px' }}>
          <h5>Selected Contract Lines:</h5>
          <div style={{ 
            maxHeight: '100px', 
            overflowY: 'auto', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            padding: '10px'
          }}>
            {contractLines.map(line => (
              <div key={line.contractline_id} style={{ fontSize: '0.9em' }}>
                • {line.contractline_name} (#{line.contractline_number})
              </div>
            ))}
          </div>
        </div>

        {/* Bulk Edit Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Price List */}
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            backgroundColor: bulkEditFields.pricelist.enabled ? '#f0f8ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={bulkEditFields.pricelist.enabled}
                onChange={() => handleFieldToggle('pricelist')}
                style={{ marginRight: '10px' }}
              />
              <label style={{ margin: 0, fontWeight: 'bold' }}>Change Price List / Plan</label>
            </div>
            
            {bulkEditFields.pricelist.enabled && (
              <>
                {hasMultipleBillingCycles ? (
                  <div className="alert alert-warning">
                    Selected lines have different billing cycles. Cannot change price list for lines with different billing cycles.
                  </div>
                ) : compatiblePriceLists.length === 0 ? (
                  <div className="alert alert-warning">
                    No compatible price lists found for the selected billing cycle.
                  </div>
                ) : (
                  <select
                    value={bulkEditFields.pricelist.value || ''}
                    onChange={(e) => handleFieldChange('pricelist', e.target.value)}
                    className="form-control"
                    disabled={!bulkEditFields.pricelist.enabled}
                  >
                    <option value="">Select a price list...</option>
                    {compatiblePriceLists.map(pl => (
                      <option key={pl.pricelist_id} value={pl.pricelist_id}>
                        {pl.name} - {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: pl.currency
                        }).format(pl.amount)} ({pl.billing_period})
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}
          </div>

          {/* Amount */}
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            backgroundColor: bulkEditFields.customAmount.enabled ? '#f0f8ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={bulkEditFields.customAmount.enabled}
                onChange={() => handleFieldToggle('customAmount')}
                style={{ marginRight: '10px' }}
              />
              <label style={{ margin: 0, fontWeight: 'bold' }}>Set Amount</label>
            </div>
            
            {bulkEditFields.customAmount.enabled && (
              <>
                <input
                  type="text"
                  value={bulkEditFields.customAmount.value}
                  onChange={(e) => handleFieldChange('customAmount', e.target.value)}
                  className="form-control"
                  placeholder="Enter amount (use . or , for decimals)"
                  disabled={!bulkEditFields.customAmount.enabled}
                />
                <small className="text-muted">
                  This will override the price list amount. Leave empty to use price list amount.
                </small>
              </>
            )}
          </div>

          {/* Tax Rate */}
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            backgroundColor: bulkEditFields.taxRate.enabled ? '#f0f8ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={bulkEditFields.taxRate.enabled}
                onChange={() => handleFieldToggle('taxRate')}
                style={{ marginRight: '10px' }}
              />
              <label style={{ margin: 0, fontWeight: 'bold' }}>Set Tax Rate</label>
            </div>
            
            {bulkEditFields.taxRate.enabled && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="number"
                    value={bulkEditFields.taxRate.value}
                    onChange={(e) => handleFieldChange('taxRate', e.target.value)}
                    className="form-control"
                    style={{ width: '100px' }}
                    step="0.01"
                    min="0"
                    max="100"
                    disabled={!bulkEditFields.taxRate.enabled}
                  />
                  <span>%</span>
                </div>
                <small className="text-muted">
                  Common rates: 0% (tax-free), 7% (reduced), 19% (standard in Germany)
                </small>
              </>
            )}
          </div>

          {/* Discount */}
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            backgroundColor: bulkEditFields.discount.enabled ? '#f0f8ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={bulkEditFields.discount.enabled}
                onChange={() => handleFieldToggle('discount')}
                style={{ marginRight: '10px' }}
              />
              <label style={{ margin: 0, fontWeight: 'bold' }}>Apply Discount</label>
            </div>
            
            {bulkEditFields.discount.enabled && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label>Discount Type:</label>
                  <select
                    value={bulkEditFields.discount.type}
                    onChange={(e) => handleDiscountChange('type', e.target.value)}
                    className="form-control"
                    disabled={!bulkEditFields.discount.enabled}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                
                <div>
                  <label>Discount Value:</label>
                  <input
                    type="number"
                    value={bulkEditFields.discount.value}
                    onChange={(e) => handleDiscountChange('value', parseFloat(e.target.value) || 0)}
                    className="form-control"
                    min="0"
                    step="0.01"
                    disabled={!bulkEditFields.discount.enabled}
                  />
                  <small className="text-muted">
                    {bulkEditFields.discount.type === 'percentage' ? 'Percentage off' : 'Amount off'}
                  </small>
                </div>
              </div>
            )}
          </div>

          {/* Start Date */}
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            backgroundColor: bulkEditFields.startDate.enabled ? '#f0f8ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={bulkEditFields.startDate.enabled}
                onChange={() => handleFieldToggle('startDate')}
                style={{ marginRight: '10px' }}
              />
              <label style={{ margin: 0, fontWeight: 'bold' }}>Set Start Date</label>
            </div>
            
            {bulkEditFields.startDate.enabled && (
              <>
                <input
                  type="date"
                  value={bulkEditFields.startDate.value}
                  onChange={(e) => handleFieldChange('startDate', e.target.value)}
                  className="form-control"
                  disabled={!bulkEditFields.startDate.enabled}
                />
                <small className="text-muted">
                  This will set the start date for all selected contract lines.
                </small>
              </>
            )}
          </div>

          {/* End Date */}
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            backgroundColor: bulkEditFields.endDate.enabled ? '#f0f8ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={bulkEditFields.endDate.enabled}
                onChange={() => handleFieldToggle('endDate')}
                style={{ marginRight: '10px' }}
              />
              <label style={{ margin: 0, fontWeight: 'bold' }}>Set End Date</label>
            </div>
            
            {bulkEditFields.endDate.enabled && (
              <>
                <input
                  type="date"
                  value={bulkEditFields.endDate.value}
                  onChange={(e) => handleFieldChange('endDate', e.target.value)}
                  className="form-control"
                  disabled={!bulkEditFields.endDate.enabled}
                />
                <small className="text-muted">
                  This will set the end date for all selected contract lines. Leave empty for no end date.
                </small>
              </>
            )}
          </div>

          {/* Trial Period */}
          <div style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '5px',
            backgroundColor: bulkEditFields.trialPeriod.enabled ? '#f0f8ff' : 'white'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={bulkEditFields.trialPeriod.enabled}
                onChange={() => handleFieldToggle('trialPeriod')}
                style={{ marginRight: '10px' }}
              />
              <label style={{ margin: 0, fontWeight: 'bold' }}>Set Trial Period</label>
            </div>
            
            {bulkEditFields.trialPeriod.enabled && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="number"
                    value={bulkEditFields.trialPeriod.value}
                    onChange={(e) => handleFieldChange('trialPeriod', e.target.value)}
                    className="form-control"
                    style={{ width: '100px' }}
                    min="0"
                    step="1"
                    disabled={!bulkEditFields.trialPeriod.enabled}
                  />
                  <span>days</span>
                </div>
                <small className="text-muted">
                  Trial period in days. Set to 0 for no trial period.
                </small>
              </>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSubmit}>
          Apply Changes
        </button>
      </div>
    </Modal>
  );
};

export default BulkEditContractLinesModal;